import { createElement, forwardRef, useMemo } from "react"
import {
  Link as TanStackLink,
  redirect as createRedirect,
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useRouter as useTanStackRouter,
} from "@tanstack/react-router"

export const Link = forwardRef<HTMLAnchorElement, { href: string; children?: React.ReactNode; [key: string]: any }>(
  ({ href, children, ...props }, ref) => {
    return createElement(TanStackLink as any, { ref, to: href, ...props }, children)
  },
)
Link.displayName = "Link"

export function usePathname(): string {
  return useLocation({ select: (location) => location.pathname })
}

export function useSearchParams(): URLSearchParams {
  const search = useLocation({ select: (location) => location.searchStr })
  return useMemo(() => new URLSearchParams(search), [search])
}

export function useRouter() {
  const navigate = useNavigate()
  const router = useTanStackRouter()

  return useMemo(
    () => ({
      push: (href: string, options?: { scroll?: boolean }) =>
        void navigate({ to: href, resetScroll: options?.scroll !== false }),
      replace: (href: string, options?: { scroll?: boolean }) =>
        void navigate({ to: href, replace: true, resetScroll: options?.scroll !== false }),
      back: () => router.history.back(),
      refresh: () => void router.invalidate(),
    }),
    [navigate, router],
  )
}

export function useParams<T extends Record<string, unknown> = Record<string, unknown>>(): T {
  const params = useRouterParams({ strict: false })
  const splat = typeof params._splat === "string" ? params._splat.split("/").filter(Boolean) : undefined
  return {
    ...params,
    ...(splat ? { slug: splat } : {}),
  } as T
}

export function redirect(href: string): never {
  throw createRedirect({ to: href })
}
